/**
 * What a teacher is holding a roster *for*.
 *
 * Not "a program". BHCS runs four separate after-school programs that all dismiss from the
 * same yard at the same time — Regular TK & K, Regular G1-G5, Mini TK, and Flexible — and
 * a teacher working the TK line is working *all* the TK children, whichever product their
 * family bought. Tabbing by program made the Mini TK and Flex children easy to miss
 * entirely: they lived behind a tab nobody had a reason to open.
 *
 * So the after-school programs collapse into one group, split the way the pickup line
 * actually splits (TK & K vs G1–G5, from the student's grade — see `gradeBand`), matching
 * the admin pickup board exactly. Every other program is its own class with its own
 * teacher and its own room, and stays its own tab.
 *
 * Chinese and Math are a third shape again. They are not programs at all — nobody enrols,
 * and there is no row to tab by — so each subject collects the grades this teacher holds
 * into one tab with a sub-tab per grade. See `virtualClasses.ts`.
 */

import { getProgramTypeIcon } from '../types/program';
import { isSampleId } from './sample/sampleData';
import {
  VIRTUAL_SUBJECTS,
  resolveVirtualClasses,
  type VirtualGrade,
  type VirtualSubject,
} from './virtualClasses';
import type { TodayProgram } from './useTodaysPrograms';

/**
 * Stands in for a program id in the roster URL. Not a UUID, so it can never collide with
 * one — the same trick `isSampleId` relies on.
 */
export const AFTER_SCHOOL_GROUP_ID = 'after-school';

export interface RosterGroup {
  /** URL segment: `after-school`, a subject id (`chinese`), or a program's own id. */
  id: string;
  label: string;
  /** Emoji shown on the tab. */
  icon: string;
  /** True when this group merges the after-school programs and splits by grade band. */
  isAfterSchool: boolean;
  /**
   * Set for a Chinese or Math tab, null for everything else.
   *
   * This — not the program type — is what decides that the group is a class rather than a
   * pickup line. `programs` below holds *after-school* programs for a virtual group, so
   * `attendanceModeFor` would read it as a pickup and ask the teacher to name a collector.
   */
  virtualSubject: VirtualSubject | null;
  /** For a virtual group, the grades this teacher holds, in roster order. Empty otherwise. */
  virtualGrades: VirtualGrade[];
  /**
   * Every program whose students belong to this group.
   *
   * For a virtual group these are the after-school programs, which is not a fudge: a
   * virtual class writes its attendance against the child's after-school `program_id`, and
   * takes its calendar — term dates, weekdays, school closures — from the same place. They
   * are the seats, not a second roster.
   */
  programs: TodayProgram[];
}

function isAfterSchool(program: TodayProgram): boolean {
  return program.type === 'after-school';
}

/**
 * Today's rosters as the tabs a teacher chooses between: one merged after-school group
 * (when any after-school program meets today), then a tab per virtual subject they teach,
 * then one tab per other program, in the order the caller supplied them.
 *
 * `afterSchoolToday` is passed separately from `programs` because the two answer different
 * questions. `programs` is what this teacher is scoped to run; `afterSchoolToday` is the
 * after-school session that virtual classes hang off, which a Chinese-only teacher needs
 * even though they are not scoped to run an after-school roster themselves.
 */
export function buildRosterGroups(
  programs: readonly TodayProgram[],
  virtualClassKeys: readonly string[] | null = null,
  afterSchoolToday: readonly TodayProgram[] = [],
): RosterGroup[] {
  const afterSchool = programs.filter(isAfterSchool);
  const groups: RosterGroup[] = [];

  if (afterSchool.length > 0) {
    groups.push({
      id: AFTER_SCHOOL_GROUP_ID,
      label: 'After School',
      icon: getProgramTypeIcon('after-school'),
      isAfterSchool: true,
      virtualSubject: null,
      virtualGrades: [],
      programs: afterSchool,
    });
  }

  // Prefer the caller's own after-school programs when it has them, so a teacher scoped to
  // a subset works the seats they are actually scoped to.
  //
  // Sample programs are excluded: a virtual class's roster comes from a SECURITY DEFINER
  // RPC that knows nothing about sample mode, so it returns real children with real
  // enrollment ids. Seating them on `sample-program-after-school` would key their
  // attendance query to ids the roster never uses — real names, no records, and no
  // check-in control on any row. With no real seats today, the subject simply has no
  // session, which is the truth.
  const realAfterSchool = afterSchool.filter(p => !isSampleId(p.id));
  const seats = realAfterSchool.length > 0 ? realAfterSchool : afterSchoolToday;
  const held = resolveVirtualClasses(virtualClassKeys);

  // No after-school session today means no Chinese or Math either: the children are not in
  // the building, and there is no seat to record attendance against.
  if (seats.length > 0) {
    for (const subject of VIRTUAL_SUBJECTS) {
      const grades = held.filter(vc => vc.subject.id === subject.id).map(vc => vc.grade);
      if (grades.length === 0) continue;
      groups.push({
        id: subject.id,
        label: subject.label,
        icon: subject.icon,
        isAfterSchool: false,
        virtualSubject: subject,
        virtualGrades: grades,
        programs: [...seats],
      });
    }
  }

  for (const program of programs) {
    if (isAfterSchool(program)) continue;
    groups.push({
      id: program.id,
      label: program.name,
      icon: getProgramTypeIcon(program.type),
      isAfterSchool: false,
      virtualSubject: null,
      virtualGrades: [],
      programs: [program],
    });
  }

  return groups;
}

/**
 * Where the portal opens. After-school wins over everything else: it is the daily program,
 * where attendance is taken every weekday, whereas an enrichment class meets once a week.
 * With no after-school today, the first group the builder produced will do.
 */
export function pickDefaultGroupId(
  programs: readonly { id: string; type: string }[],
  groups: readonly RosterGroup[] = [],
): string | null {
  if (programs.some(p => p.type === 'after-school')) return AFTER_SCHOOL_GROUP_ID;
  return groups[0]?.id ?? programs[0]?.id ?? null;
}

/**
 * The group a URL segment means, or null when it means nothing today.
 *
 * A link to an individual after-school program — a bookmark from before the merge, or an
 * old email — resolves to the merged group rather than 404ing, because that group is where
 * those students now are.
 */
export function resolveGroup(
  groupId: string | undefined,
  programs: readonly TodayProgram[],
  virtualClassKeys: readonly string[] | null = null,
  afterSchoolToday: readonly TodayProgram[] = [],
): RosterGroup | null {
  const groups = buildRosterGroups(programs, virtualClassKeys, afterSchoolToday);
  if (!groupId) return null;

  const exact = groups.find(group => group.id === groupId);
  if (exact) return exact;

  const program = programs.find(p => p.id === groupId);
  if (program && isAfterSchool(program)) {
    return groups.find(group => group.id === AFTER_SCHOOL_GROUP_ID) ?? null;
  }
  return null;
}
